import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';
import { Project } from '../types';
import { useData } from '../contexts/DataContext';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const ModalContent = styled(motion.div)`
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(64, 224, 255, 0.3);
  border-radius: 20px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(64, 224, 255, 0.1);
  backdrop-filter: blur(20px);

  @media (max-width: 768px) {
    border-radius: 15px;
    max-height: 95vh;
  }
  
  @media (max-width: 480px) {
    border-radius: 10px;
    max-height: 98vh;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    color: #ff6b6b;
    background: rgba(255, 107, 107, 0.1);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 1.3rem;
    top: 0.75rem;
    right: 0.75rem;
  }
`;

const ModalHeader = styled.div`
  padding: 3rem 3rem 2rem;
  text-align: center;
  border-bottom: 1px solid rgba(64, 224, 255, 0.1);

  @media (max-width: 768px) {
    padding: 2.5rem 1.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 2rem 1.25rem 1.25rem;
  }
`;

const ProjectTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 1rem 0;
  background: linear-gradient(135deg, #ffffff 0%, #40e0ff 50%, #ffffff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
  }
`;


const ModalBody = styled.div`
  padding: 2rem 3rem 3rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 2rem;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem 1.25rem 1.75rem;
    gap: 1.25rem;
  }
`;

const DescriptionSection = styled.div`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  font-size: 1.05rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.7;
  }
`;

const KeyResultsSection = styled.div`
  padding: 1rem 0;
  border-top: 1px solid rgba(64, 224, 255, 0.15);
  border-bottom: 1px solid rgba(64, 224, 255, 0.15);
  
  @media (max-width: 768px) {
    padding: 0.875rem 0;
  }
`;

const ResultsTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(64, 224, 255, 0.8);
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const ResultsList = styled.div`
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.7;
  font-size: 0.95rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.6;
  }
`;

const ResultItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '•';
    color: rgba(64, 224, 255, 0.6);
    margin-right: 0.75rem;
    margin-top: 0.1rem;
    flex-shrink: 0;
    font-weight: normal;
  }
`;

const TechnologiesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 480px) {
    gap: 0.6rem;
  }
`;

const TechnologyTag = styled.span<{ category?: string }>`
  background: ${props => {
    if (!props.category) return 'rgba(64, 224, 255, 0.15)';
    switch (props.category) {
      case 'Programming & Query Languages': return 'rgba(25, 118, 210, 0.15)';
      case 'Analytics & Data Platforms': return 'rgba(123, 31, 162, 0.15)';
      case 'Business Systems and Tools': return 'rgba(56, 142, 60, 0.15)';
      case 'Data experience': return 'rgba(245, 124, 0, 0.15)';
      default: return 'rgba(64, 224, 255, 0.15)';
    }
  }};
  color: ${props => {
    if (!props.category) return '#40e0ff';
    switch (props.category) {
      case 'Programming & Query Languages': return '#1976d2';
      case 'Analytics & Data Platforms': return '#7b1fa2';
      case 'Business Systems and Tools': return '#388e3c';
      case 'Data experience': return '#f57c00';
      default: return '#40e0ff';
    }
  }};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid ${props => {
    if (!props.category) return 'rgba(64, 224, 255, 0.25)';
    switch (props.category) {
      case 'Programming & Query Languages': return 'rgba(25, 118, 210, 0.25)';
      case 'Analytics & Data Platforms': return 'rgba(123, 31, 162, 0.25)';
        case 'Business Systems and Tools': return 'rgba(56, 142, 60, 0.25)';
        case 'Data experience': return 'rgba(245, 124, 0, 0.25)';
      default: return 'rgba(64, 224, 255, 0.25)';
    }
  }};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => {
      if (!props.category) return 'rgba(64, 224, 255, 0.3)';
      switch (props.category) {
        case 'Programming & Query Languages': return 'rgba(25, 118, 210, 0.3)';
        case 'Analytics & Data Platforms': return 'rgba(123, 31, 162, 0.3)';
        case 'Business Systems and Tools': return 'rgba(56, 142, 60, 0.3)';
        case 'Data experience': return 'rgba(245, 124, 0, 0.3)';
        default: return 'rgba(64, 224, 255, 0.3)';
      }
    }};
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.85rem;
    font-size: 0.85rem;
    border-radius: 18px;
  }
`;

interface ProjectModalProps {
  project: Project;
  onClose: () => void;
}

const ProjectModal: React.FC<ProjectModalProps> = ({ project, onClose }) => {
  const { skills } = useData();

  // Helper function to get category for a skill
  const getSkillCategory = (skillName: string): string => {
    const skill = skills.find(s => s.name === skillName);
    return skill?.category || '';
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContent
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.4 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>

          <ModalHeader>
            <ProjectTitle>{project.title}</ProjectTitle>
          </ModalHeader>

          <ModalBody>
            <DescriptionSection>
              {project.description}
            </DescriptionSection>

            {project.keyResults && (
              <KeyResultsSection>
                <ResultsTitle>
                  Key Results
                </ResultsTitle>
                <ResultsList>
                  {project.keyResults.split('\n').filter(line => line.trim()).map((line, index) => {
                    // Check if line looks like a bullet point
                    const cleanedLine = line.trim().replace(/^[-•*]\s*/, '');
                    return (
                      <ResultItem key={index}>
                        {cleanedLine}
                      </ResultItem>
                    );
                  })}
                </ResultsList>
              </KeyResultsSection>
            )}

            <TechnologiesGrid>
              {project.technologies.map((tech, index) => (
                <TechnologyTag key={index} category={getSkillCategory(tech)}>
                  {tech}
                </TechnologyTag>
              ))}
            </TechnologiesGrid>

          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </AnimatePresence>
  );
};

export default ProjectModal;
